/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MenuSwitcherIcon } from '@coursera/cds-icons';

import { useSideNavCollapse } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavCollapseProvider';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  container: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px var(--cds-spacing-200);
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    flex-shrink: 0;
  `,
  containerCollapsed: css`
    justify-content: center;
    padding: 20px 0;
  `,
  logo: css`
    display: flex;
    align-items: center;
    height: 20px;
    flex: 1;
    min-width: 0;
  `,
  toggleButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--cds-spacing-400);
    height: var(--cds-spacing-400);
    background: transparent;
    border: none;
    border-radius: var(--cds-border-radius-50);
    cursor: pointer;
    color: var(--cds-color-neutral-primary-weak);
    transition: background-color 200ms ease;

    /* Match the hover surface used by SideNavItem / MenuItem / OrgContextItem. */
    &:hover,
    &:focus-visible {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
};

type Props = {
  logo?: React.ReactNode;
};

/**
 *
 * The SideNavHeader component is used to render the header of the sidebar.
 * @param logo - The logo to render in the header
 * @returns The SideNavHeader component
 */
export const SideNavHeader = ({ logo }: Props) => {
  const { isSideNavCollapsed, toggle } = useSideNavCollapse();

  return (
    <div css={[styles.container, isSideNavCollapsed && styles.containerCollapsed]}>
      {!isSideNavCollapsed && <div css={styles.logo}>{logo}</div>}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!isSideNavCollapsed}
        aria-label={isSideNavCollapsed ? _t('Expand navigation') : _t('Collapse navigation')}
        css={styles.toggleButton}
      >
        {/* TODO: Replace the icon with actual icon from Figma, which is not available yet. */}
        <MenuSwitcherIcon size="medium" />
      </button>
    </div>
  );
};

export default SideNavHeader;
